import { getDateFromOfferedType, getHeightFromDate, getTopFromDate, IMeeting, IMeetingConfirmation, useViewModel } from '@utils';
import { CalendarViewModel, MeetingPlanerViewModel } from '@views';
import { observer } from 'mobx-react-lite';
import React, { FC, MouseEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/components/meeting.scss';
import { AcceptOfferedTimeButtons } from './components/AcceptOfferedTimeButtons';
import { MeetingViewModel } from '../calendar';
import { useNavigate } from 'react-router-dom';

interface OfferedTimeProps {
  confirmation: IMeetingConfirmation;
  meeting: IMeeting;
}

/**
 * @meeting объект встречи
 */
export const OfferedTime: FC<OfferedTimeProps> = observer(({ meeting, confirmation }) => {
  const meetingViewModel = useViewModel(MeetingViewModel);
  const calendarViewModel = useViewModel(CalendarViewModel);
  const meetingPlanerViewModel = useViewModel(MeetingPlanerViewModel);

  const startOffer = getDateFromOfferedType(confirmation?.offeredTime);
  const duration = +meeting?.endDate - +meeting?.startDate;
  const endOffer = new Date(+startOffer + duration);

  let navigate = useNavigate();
  const height = getHeightFromDate(startOffer, endOffer);

  const [offerTimeHeight, setOfferTimeHeight] = useState(height);
  const top = getTopFromDate(startOffer, calendarViewModel.startHour);

  const isMeetingInOneLine = useMemo(() =>
    calendarViewModel.isMeetingInOneLine(offerTimeHeight), [startOffer, endOffer, offerTimeHeight]);

  const isShortMeeting = useMemo(() =>
    calendarViewModel.isShortMeeting(offerTimeHeight), [startOffer, endOffer, offerTimeHeight]);

  const [topCoord, setTopCoord] = useState(top);
  const [zIndex, setZIndex] = useState(10);
  const ref = useRef<HTMLDivElement>();
  
  const onAcceptOfferedTime = () => {
    meetingViewModel.onUpdateMeeting('startDate', startOffer);
    meetingViewModel.onUpdateMeeting('endDate', endOffer);
    meetingViewModel.answerTimeOffer(true, confirmation.meetingConfirmationId);
    meetingViewModel.resetMeetingData();
    return navigate("/");
  }

  const onCancelOfferedTime = () => meetingViewModel
    .answerTimeOffer(false, confirmation.meetingConfirmationId)
    .then(() => {
      meetingPlanerViewModel.cancelOffer(confirmation.userId);
    });
  

  const start = useMemo(() => calendarViewModel.getTimePoint(startOffer), []);
  const end = useMemo(() => calendarViewModel.getTimePoint(endOffer), []);

  let timer: NodeJS.Timeout;

  const onMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
    const isReverse = window.innerHeight - ref?.current.getBoundingClientRect().bottom < 60;
    if (e.type === 'mouseenter' && height < 60) {
      timer = setTimeout(() => {
        setOfferTimeHeight(height + 60);
        isReverse && setTopCoord(prev => prev - 60);
        setZIndex(500);
      }, 300);
    }
    else {
      setOfferTimeHeight(height);
      isReverse && setTopCoord(top);
      setZIndex(10);
      clearTimeout(timer);
    }
  };

  const title = useMemo(() =>
    `Предлагаю провести \n${meeting.meetingName}\nc ${start} до ${end}`
    , [meeting.meetingName, start, end]);

  useEffect(() => setTopCoord(top), [top]);

  return (
    <div
      ref={ref}
      onMouseEnter={onMouseMove}
      onMouseLeave={onMouseMove}
      className="meeting offered"
      title={title}
      style={{
        top: topCoord,
        height: offerTimeHeight,
        zIndex,
        width: '100%',
        gap: isShortMeeting ? 6 : 8,
        flexDirection: isMeetingInOneLine ? 'row-reverse' : 'column',
        justifyContent: isMeetingInOneLine ? 'flex-end' : 'flex-start',
        padding: `${isShortMeeting ? 3 : 12}px 14px`
      }}
    >
      <div className="name">
        {meeting.meetingName}
      </div>
      <div className="time">
        {start}{offerTimeHeight >= 30 ? `-  ${end}` : ''}
      </div>
      {offerTimeHeight > 60 && <AcceptOfferedTimeButtons 
        height={offerTimeHeight}
        onAcceptTime={onAcceptOfferedTime}
        onCancel={onCancelOfferedTime}
      />}
    </div>
  );
});